import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { OpenInNew } from '@mui/icons-material';

import { ILenderListItem } from '@modules/admin/Tools/shared/LenderControlsHeader';
import { getOptions, withQueryParams } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { useLenderId } from '@shared/hooks/useLenderId';
import { StyledNewTabButton, Wrapper } from '@styles/CommonStyles';
import { SelectLenderWrapper, TabsPageHeader } from '@styles/Tools/LenderControl/LenderControlsHeaderStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { useQueryState } from '@shared/hooks/useQueryState';
import { getSelectActiveTabIndex } from '@store/reducers/tabsSlice';

import { LenderInfoTabs } from './Tabs/LenderInfoTabs';

export const LenderInfo = () => {
  const navigate = useNavigate();
  const lenderId = useLenderId();
  const [showOnlyActiveLenderTabQuery, setShowOnlyActiveLenderTabQuery] = useQueryState('showOnlyActiveLenderTab');
  const activeTab = useSelector(getSelectActiveTabIndex);
  const { closeLeftTabs, closeRightTabs, updateCurrentTab } = useTabs();

  const { data: lendersList } = useSWR<ILenderListItem[]>(`${SERVICES.LENDER}/lenders`);

  const setLenderId = useCallback((lenderId: string) => {
    const LenderName = lendersList?.find(lender => lender.id === lenderId)?.name ?? '';
    const route = withQueryParams('/lender-info', { lenderId });
    updateCurrentTab({ 
      route, 
      title: `${LenderName} - LenderInfo`,
    });
    navigate(route);
  }, [lendersList]);

  useEffect(() => {
    if (showOnlyActiveLenderTabQuery) {
      closeRightTabs(activeTab);
      closeLeftTabs(activeTab);
      const lenderName = lendersList?.find((lender) => lender.id === lenderId)?.name ?? '';
      const route = withQueryParams('/lender-info', { lenderId });
      updateCurrentTab({
        route,
        title: `${lenderName} - LenderInfo`,
      });
    }
    return () => {
      setShowOnlyActiveLenderTabQuery(undefined);
    };
  }, [showOnlyActiveLenderTabQuery, activeTab, lenderId, lendersList]);
  
  const options = useMemo(() => (
    getOptions(lendersList).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
  ), [lendersList]);

  const openInNewTab = () => {
    const route = withQueryParams('/lender-info', { lenderId: lenderId, showOnlyActiveLenderTab: true }, false);
    window.open(route, '_blank');
  };

  return (
    <Wrapper>
      <TabsPageHeader>
        <SelectLenderWrapper>
          <SelectAutocomplete
            label="Lender"
            canBeClean={false}
            value={lenderId}
            options={options}
            onChange={setLenderId}
          />
        </SelectLenderWrapper>
        <StyledNewTabButton
          variant="contained"
          startIcon={<OpenInNew />}
          onClick={() => openInNewTab()}
        />
      </TabsPageHeader>

      <LenderInfoTabs lenderId={lenderId} />
    </Wrapper>
  );
};
